"use client";

import { usePathname } from "next/navigation";
import type { JSX } from "react";
import { useCallback, useEffect, useRef } from "react";
import { Icon, Image, Link, Text, View } from "@/components";
import type { AsideProps } from "@/components/types";
import useWindowSize from "@/hooks/useWindowSize";
import { useLayout } from "@/providers";

/**
 * @name SidebarTwoColumn
 * @description Renders a two-column sidebar layout.
 * NOTE: Refactored to support flat `MenuItem[]` structure.
 * Currently behaves similarly to the Vertical sidebar but preserves the file/component existence.
 */
const SidebarTwoColumn = ({
  data,
  setSidebar,
  sidebarIsOpen
}: AsideProps): JSX.Element => {
  const path = usePathname();
  const { t } = useLayout();
  const { windowSize } = useWindowSize();
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const currentWindowSize = window.innerWidth;
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        if (currentWindowSize < 1400) {
          setSidebar(false);
        }
      }
    },
    [setSidebar]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <aside
      className={`w-[280px] xxxl:w-[320px] z-[21] bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 ${
        sidebarIsOpen
          ? "translate-x-0 visible"
          : "ltr:-translate-x-full rtl:translate-x-full invisible"
      } duration-300 sidebar fixed ltr:left-0 rtl:right-0 h-full top-0`}
      ref={sidebarRef}
    >
      <div className="flex flex-col h-full">
        {/* Logo Area */}
        <div className="p-6 border-b border-gray-50 dark:border-gray-800">
          <Link href="/" className="flex items-center gap-3">
            <Image
              width={36}
              height={36}
              src="/icons/icon-96x96.png"
              alt="logo"
            />
            <span className="text-xl font-bold text-gray-800 dark:text-white">
              Intranet
            </span>
          </Link>
        </div>

        {/* Menu Items */}
        <div className="overflow-y-auto flex-1 p-4">
          <View type="ul" className="flex flex-col gap-2">
            {data?.map((item: AsideProps["data"][number]) => {
              const isActive = path === String(item.value);

              return (
                <Link
                  key={item.id}
                  href={String(item.value)}
                  target={item.target ?? "_self"}
                  onClick={() => {
                    if (windowSize && windowSize < 1400) setSidebar(false);
                  }}
                  className={`
                        flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-200
                        ${
                          isActive
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800"
                        }
                      `}
                >
                  <View
                    className={`w-6 h-6 flex justify-center items-center text-lg z-10 shrink-0 ${isActive ? "active" : ""}`}
                  >
                    <Icon name={`${item.icon}`} />
                  </View>
                  <Text
                    className={`ml-4 text-base tracking-wide font-medium whitespace-nowrap xxl:hidden group-hover:block ${isActive ? "font-semibold" : ""}`}
                  >
                    {t(item.label || "")}
                  </Text>
                </Link>
              );
            })}
          </View>
        </div>
      </div>
    </aside>
  );
};

export default SidebarTwoColumn;
